import React, { useState } from "react";
import { useGetBillingEventsQuery } from "@/store/admin_api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { BillingEvent } from "@/model/interface/BillingEvent";

const TYPE_BADGES: Record<BillingEvent["type"], { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  PAYMENT_FAILED: { label: "Payment failed", variant: "secondary" },
  SUSPENDED: { label: "Suspended", variant: "destructive" },
  RESTORED: { label: "Restored", variant: "default" },
  CANCELED: { label: "Canceled", variant: "outline" },
};

export function BillingEventsTab() {
  const [page, setPage] = useState(0);
  const { data: events, isLoading } = useGetBillingEventsQuery({ page, size: 50 });

  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing Activity</CardTitle>
        <CardDescription>
          Dunning, suspensions, restorations, and reconciliation actions across all organisations.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!events || events.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No billing events yet. Events appear here when payments fail, grace periods expire, subscriptions
            are restored, or the nightly reconciliation intervenes.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.items.map((event) => {
                const badge = TYPE_BADGES[event.type] ?? { label: event.type, variant: "outline" as const };
                return (
                  <TableRow key={event.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {format(new Date(event.createdAt), "MMM dd, yyyy HH:mm")}
                    </TableCell>
                    <TableCell className="font-medium">{event.organisation?.name ?? event.organisation?.id}</TableCell>
                    <TableCell>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{event.reason}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {events && events.totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Page {events.page + 1} of {events.totalPages}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={events.last} onClick={() => setPage(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
